import {useForm} from 'react-hook-form';
import {useId} from 'react';
import {Form} from '@ui/forms/form';
import {AccountSettingsPanel} from '@common/auth/ui/account-settings/account-settings-panel';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {UpdatePasswordPayload, useUpdatePassword} from './use-update-password';
import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {AccountSettingsId} from '@common/auth/ui/account-settings/account-settings-sidenav';
import {useState} from 'react';

export function ChangePasswordPanel() {
  const form = useForm<UpdatePasswordPayload>();
  const formId = useId();
  const updatePassword = useUpdatePassword(form);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  return (
    <AccountSettingsPanel
      id={AccountSettingsId.Password}
      title={<Trans message="Update password" />}
      actions={
        <Button
          type="submit"
          form={formId}
          variant="flat"
          color="primary"
          disabled={!form.formState.isValid || updatePassword.isPending}
        >
          <Trans message="Update password" />
        </Button>
      }
    >
      <Form
        form={form}
        id={formId}
        onSubmit={newValues => {
          updatePassword.mutate(newValues, {
            onSuccess: () => {
              form.reset();
            },
          });
        }}
      >

         <FormTextField
        className="mb-24"
        name="current_password"
        label={<Trans message="Current password" />}
        type={showCurrent ? 'text' : 'password'}
        autoComplete="current-password"
        required
        endAdornment={
          <button
            type="button"
            onClick={() => setShowCurrent(!showCurrent)}
            className="ml-8 pr-4 text-gray-600 hover:text-primary focus:outline-none"
            tabIndex={-1}
          >
            {showCurrent ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 
                            4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M3 3l18 18" />
                  </svg>
                  ) : (
                    // Eye Open SVG (larger)
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 
                            4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z" />
                  </svg>
            )}
          </button>
        }
      />

      <FormTextField
        className="mb-24"
        name="password"
        label={<Trans message="New password" />}
        type={showNew ? 'text' : 'password'}
        autoComplete="new-password"
        required
        endAdornment={
          <button
            type="button"
            onClick={() => setShowNew(!showNew)}
            className="ml-8 pr-4 text-gray-600 hover:text-primary focus:outline-none"
            tabIndex={-1}
          >
            {showNew ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 
                            4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M3 3l18 18" />
                  </svg>
                  ) : (
                    // Eye Open SVG (larger)
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 
                            4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z" />
                  </svg>
            )}
          </button>
        }
      />

      <FormTextField
        name="password_confirmation"
        label={<Trans message="Confirm password" />}
        type={showConfirm ? 'text' : 'password'}
        autoComplete="new-password"
        required
        endAdornment={
          <button
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
            className="ml-8 pr-4 text-gray-600 hover:text-primary focus:outline-none"
            tabIndex={-1}
          >
            {showConfirm ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 
                            4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M3 3l18 18" />
                  </svg>
                  ) : (
                    // Eye Open SVG (larger)
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 
                            4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z" />
                  </svg>
            )}
          </button>
        }
      />
      </Form>
    </AccountSettingsPanel>
  );
}